import { Ripple } from "primereact/ripple";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

type ImagePreviewModalProps = {
    open: boolean;
    src: string;
    alt?: string;
    title?: string;
    onClose: () => void;
    toolbarRightSlot?: React.ReactNode;
    onDelete?: () => void;
    hasDelete?: boolean;
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
    open,
    src,
    alt,
    title,
    onClose,
    toolbarRightSlot,
    hasDelete=false,
    onDelete,
}) => {
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    
    const dragStartRef = useRef({ x: 0, y: 0 });
    const stageRef = useRef<HTMLDivElement | null>(null);

    const [isMounted, setIsMounted] = useState(open);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (open) {
            setScale(1);
            setRotate(0);
            setPosition({ x: 0, y: 0 });
            setIsDragging(false);
            setIsMounted(true);
            const timer = setTimeout(() => setIsVisible(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
            const timer = setTimeout(() => setIsMounted(false), 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    const canRender = isMounted && !!src;

    const reset = useCallback(() => {
        setScale(1);
        setRotate(0);
        setPosition({ x: 0, y: 0 });
    }, []);

    const zoomIn = useCallback(() => setScale((s) => clamp(Number((s + 0.2).toFixed(2)), 0.5, 4)), []);
    const zoomOut = useCallback(() => setScale((s) => clamp(Number((s - 0.2).toFixed(2)), 0.5, 4)), []);
    const rotateLeft = useCallback(() => setRotate((r) => r - 90), []);
    const rotateRight = useCallback(() => setRotate((r) => r + 90), []);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
        e.preventDefault();
        setIsDragging(true);
        dragStartRef.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        };
    }, [position]);

    const handleTouchStart = useCallback((e: React.TouchEvent<HTMLImageElement>) => {
        if (e.touches.length !== 1) return;
        setIsDragging(true);
        dragStartRef.current = {
            x: e.touches[0].clientX - position.x,
            y: e.touches[0].clientY - position.y,
        };
    }, [position]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStartRef.current.x,
            y: e.clientY - dragStartRef.current.y,
        });
    }, [isDragging]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (!isDragging) return;
        if (e.touches.length !== 1) return;
        e.preventDefault();
        setPosition({
            x: e.touches[0].clientX - dragStartRef.current.x,
            y: e.touches[0].clientY - dragStartRef.current.y,
        });
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (!isVisible || !isDragging) return;

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleMouseUp);
        };
    }, [isVisible, isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

    useEffect(() => {
        if (!isVisible) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "+" || e.key === "=") zoomIn();
            if (e.key === "-" || e.key === "_") zoomOut();
            if (e.key.toLowerCase() === "r") rotateRight();
            if (e.key.toLowerCase() === "l") rotateLeft();
            if (e.key.toLowerCase() === "0") reset();
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isVisible, onClose, zoomIn, zoomOut, rotateRight, rotateLeft, reset]);

    useEffect(() => {
        if (!isVisible) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isVisible]);

    useEffect(() => {
        if (!isVisible) return;
        const el = stageRef.current;
        if (!el) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            if (e.deltaY < 0) zoomIn();
            else zoomOut();
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return () => el.removeEventListener("wheel", onWheel as any);
    }, [isVisible, zoomIn, zoomOut]);

    const transformStyle = useMemo(
        () => ({
            transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale}) rotate(${rotate}deg)`,
            cursor: isDragging ? "grabbing" : (scale > 1 || position.x !== 0 || position.y !== 0 ? "grab" : "auto"),
            transition: isDragging ? "none" : undefined,
        }),
        [scale, rotate, position, isDragging]
    );

    if (!canRender) return null;

    return ReactDOM.createPortal(
        <div className={`fxpv-overlay ${isVisible ? "visible" : ""}`} role="dialog" aria-modal="true" aria-label={title || "Image preview"}>
            <div className="fxpv-backdrop" onClick={onClose} />

            <div className="fxpv-wrap">
                <div className="fxpv-toolbar" aria-label="Preview controls">
                    {toolbarRightSlot}

                    <button className="fxpv-iconbtn p-ripple" onClick={rotateRight} type="button" aria-label="Rotate right" title="Rotate Right (R)">
                        <i className="bi bi-arrow-clockwise" />
                        <Ripple />
                    </button>

                    <button className="fxpv-iconbtn p-ripple" onClick={rotateLeft} type="button" aria-label="Rotate left" title="Rotate Left (L)">
                        <i className="bi bi-arrow-counterclockwise" />
                        <Ripple />
                    </button>

                    <button className="fxpv-iconbtn p-ripple" onClick={zoomOut} type="button" aria-label="Zoom out" title="Zoom Out (-)">
                        <i className="bi bi-zoom-out" />
                        <Ripple />
                    </button>

                    <button className="fxpv-iconbtn p-ripple" onClick={zoomIn} type="button" aria-label="Zoom in" title="Zoom In (+)">
                        <i className="bi bi-zoom-in" />
                        <Ripple />
                    </button>

                       {hasDelete && (
                        <button className="fxpv-iconbtn p-ripple fxpv-delete" onClick={onDelete} type="button" aria-label="Delete" title="Delete (D)">
                            <i className="bi bi-trash" />
                            <Ripple />
                        </button>
                    )}
                 

                    <button className="fxpv-iconbtn p-ripple fxpv-close" onClick={onClose} type="button" aria-label="Close" title="Close (Esc)">
                        <i className="bi bi-x-lg" />
                        <Ripple />
                    </button>

                </div>

                <div className="fxpv-frame">
                    <div className="fxpv-stage" ref={stageRef} onClick={onClose}>
                        <img
                            src={src}
                            alt={alt || "preview"}
                            className="fxpv-img"
                            style={transformStyle}
                            draggable={false}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleTouchStart}
                        />
                    </div>

                    <div className="fxpv-hint">
                        <span>Scroll to zoom</span>
                        <span className="fxpv-dot" />
                        <button className="fxpv-link" type="button" onClick={reset}>
                            Reset (0)
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};